import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, IconButton, Grid, CircularProgress, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { componentService } from '@/services';
import { ComponentConfiguration, LineGate } from '@core/types';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FieldWithIcon from '@/components/form/FieldWithIcon.tsx';
import { nameValidationRules, required } from '@core/utils/validation.ts';
import { WcnCheckbox } from '@/components';

interface LineGatesFieldsProps {
    value: LineGate[];
    shopId: string;
    onChange: (gates: LineGate[]) => void;
}

const LineGatesFields: React.FC<LineGatesFieldsProps> = ({ value, shopId, onChange }) => {
    const { t } = useTranslation();
    const [lineGates, setLineGates] = useState<LineGate[]>(value);
    const [cameras, setCameras] = useState<ComponentConfiguration[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSensorCameras = async () => {
            setLoading(true);
            try {
                const response = await componentService.loadSensorCamerasForShop(shopId);
                setCameras(response || []);
            } catch (error: any) {
                console.error('Failed to load sensor cameras', error);
            } finally {
                setLoading(false);
            }
        };

        if (shopId) {
            loadSensorCameras();
        }
    }, [shopId]);

    useEffect(() => {
        setLineGates(value);
    }, [value]);

    const getCameraLines = useMemo(() => {
        return (sensorCameraId: string | null) => {
            if (!sensorCameraId) return [];
            const camera = cameras.find((c) => c.id === sensorCameraId);
            return camera?.lines || [];
        };
    }, [cameras]);

    const handleGateChange = useCallback(
        (index: number, field: keyof LineGate, value: any) => {
            setLineGates((prev) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], [field]: value };
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const handleCameraChange = useCallback(
        (index: number, value: any) => {
            setLineGates((prev) => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    sensorCameraId: value,
                    elementId: null
                };
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const handleBackupCameraChange = useCallback(
        (index: number, value: any) => {
            setLineGates((prev) => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    backupSensorCameraId: value,
                    backupElementId: null
                };
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    const handleAddGate = useCallback(() => {
        setLineGates((prev) => {
            const updated = [
                ...prev,
                {
                    sensorCameraId: null,
                    elementId: null,
                    elementName: '',
                    isReversed: false
                }
            ];
            onChange(updated);
            return updated;
        });
    }, [onChange]);

    const handleDeleteGate = useCallback(
        (index: number) => {
            setLineGates((prev) => {
                const updated = prev.filter((_, i) => i !== index);
                onChange(updated);
                return updated;
            });
        },
        [onChange]
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {lineGates.map((lineGate, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <FormTextField
                            name={`elementName_${index}`}
                            label={t('name')}
                            value={lineGate.elementName || ''}
                            onChange={(e) => handleGateChange(index, 'elementName', e.target.value)}
                            fullWidth
                            required
                            rules={nameValidationRules(t)}
                        />
                    </Grid>
                    <Grid item md={6} sx={{ display: { xs: 'none', md: 'block' } }} />
                    <Grid item xs={12} md={6}>
                        <FieldWithIcon
                            select={
                                <FormSelect
                                    name={`sensorCameraId_${index}`}
                                    label={t('sensorCamera')}
                                    value={lineGate?.sensorCameraId || ''}
                                    items={cameras}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(value) => handleCameraChange(index, value)}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                    placeholder="-"
                                />
                            }
                            icon={
                                !lineGate?.sensorCameraId && index > 0 ? (
                                    <IconButton onClick={() => handleDeleteGate(index)} sx={{ fontSize: 18 }}>
                                        <i className="bi bi-trash3" />
                                    </IconButton>
                                ) : null
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {lineGate.sensorCameraId && (
                            <FieldWithIcon
                                select={
                                    <FormSelect
                                        name={`line_${index}`}
                                        label={t('line')}
                                        value={lineGate.elementId || ''}
                                        items={getCameraLines(lineGate.sensorCameraId).map((line) => ({
                                            value: line
                                        }))}
                                        itemText="value"
                                        itemValue="value"
                                        onChange={(value) => handleGateChange(index, 'elementId', value)}
                                        rules={[required(t)]}
                                        required
                                        sx={{ width: '100%' }}
                                        placeholder="-"
                                    />
                                }
                                icon={
                                    index > 0 ? (
                                        <IconButton onClick={() => handleDeleteGate(index)} sx={{ fontSize: 18 }}>
                                            <i className="bi bi-trash3" />
                                        </IconButton>
                                    ) : null
                                }
                            />
                        )}
                    </Grid>
                    {lineGate.sensorCameraId && (
                        <>
                            <Grid item xs={12} md={6}>
                                <FormSelect
                                    name={`sensorCameraBackupId_${index}`}
                                    label={t('optionalFallbackCamera')}
                                    value={lineGate?.backupSensorCameraId || ''}
                                    items={cameras.filter((c) => c.id !== lineGate.sensorCameraId)}
                                    itemText="name"
                                    itemValue="id"
                                    placeholder="-"
                                    onChange={(value) => handleBackupCameraChange(index, value)}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            {lineGate.backupSensorCameraId && (
                                <Grid item xs={12} md={6}>
                                    <FieldWithIcon
                                        select={
                                            <FormSelect
                                                name={`line_fallback_${index}`}
                                                label={t('line')}
                                                value={lineGate.backupElementId || ''}
                                                items={getCameraLines(lineGate.backupSensorCameraId).map((line) => ({
                                                    value: line
                                                }))}
                                                itemText="value"
                                                itemValue="value"
                                                onChange={(value) => handleGateChange(index, 'backupElementId', value)}
                                                rules={[required(t)]}
                                                required
                                                sx={{ width: '100%' }}
                                            />
                                        }
                                        icon={
                                            <IconButton
                                                onClick={() => handleBackupCameraChange(index, null)}
                                                sx={{ fontSize: 18 }}
                                            >
                                                <i className="bi bi-trash3" />
                                            </IconButton>
                                        }
                                    />
                                </Grid>
                            )}
                        </>
                    )}

                    {lineGate.sensorCameraId && (
                        <Grid item xs={12} md={12}>
                            <WcnCheckbox
                                label={t('countBackwards')}
                                checked={lineGate.isReversed}
                                onChange={(e) => handleGateChange(index, 'isReversed', e.target.checked)}
                                className="wcn-checkbox--16"
                            />
                        </Grid>
                    )}
                </Grid>
            ))}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 1 }}>
                <Button
                    variant="text"
                    startIcon={<i className="bi bi-plus" />}
                    onClick={handleAddGate}
                    sx={{ textTransform: 'none' }}
                >
                    {t('addSensorCamera')}
                </Button>
            </Box>
        </Box>
    );
};

export default LineGatesFields;
